import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useWhoopConnection } from '@/hooks/useWhoopDataCached';

export interface WhoopData {
  recovery_score: number | null;
  hrv_score: number | null;
  resting_heart_rate: number | null;
  sleep_performance: number | null;
  sleep_duration_minutes: number | null;
  sleep_quality_score: number | null;
  strain_score: number | null;
  calories_burned: number | null;
}

export interface WhoopAutoFillData {
  // Sleep fields
  sleep_duration?: number; // hours
  sleep_quality?: number; // 1-10 scale
  hours_slept?: number;
  
  // Recovery/Energy fields
  energy_level?: number; // 1-10 scale based on recovery
  rest_quality?: string;
  
  // Exercise/Strain fields
  exercise_intensity?: number; // 1-10 based on strain
  
  // Raw WHOOP data for display
  raw: WhoopData | null;
}

export function useWhoopAutoFill() {
  const today = format(new Date(), 'yyyy-MM-dd');
  
  // Use cached connection status
  const { data: connectionInfo } = useWhoopConnection();

  // Fetch today's WHOOP data with caching
  const { data: whoopData, isLoading } = useQuery({
    queryKey: ['whoop-autofill', today],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('whoop_data')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching WHOOP data:', error);
        return null;
      }
      return data as WhoopData | null;
    },
    enabled: connectionInfo?.isConnected === true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
  });

  // Transform WHOOP data to check-in fields
  const getAutoFillData = (): WhoopAutoFillData => {
    if (!whoopData) {
      return { raw: null };
    }

    const autoFill: WhoopAutoFillData = { raw: whoopData };

    // Sleep duration (convert minutes to hours)
    if (whoopData.sleep_duration_minutes) {
      autoFill.sleep_duration = Math.round((whoopData.sleep_duration_minutes / 60) * 10) / 10;
      autoFill.hours_slept = Math.round(whoopData.sleep_duration_minutes / 60);
    }

    // Sleep quality (WHOOP sleep performance is 0-100, convert to 1-10)
    if (whoopData.sleep_performance) {
      autoFill.sleep_quality = Math.round(whoopData.sleep_performance / 10);
    } else if (whoopData.sleep_quality_score) {
      autoFill.sleep_quality = Math.round(whoopData.sleep_quality_score / 10);
    }

    // Energy level from recovery score (0-100 -> 1-10)
    if (whoopData.recovery_score) {
      autoFill.energy_level = Math.max(1, Math.round(whoopData.recovery_score / 10));
      
      // Rest quality description based on recovery
      if (whoopData.recovery_score >= 67) {
        autoFill.rest_quality = 'well_rested';
      } else if (whoopData.recovery_score >= 34) {
        autoFill.rest_quality = 'moderate';
      } else {
        autoFill.rest_quality = 'tired';
      }
    }

    // Exercise intensity from strain (0-21 scale -> 1-10)
    if (whoopData.strain_score) {
      autoFill.exercise_intensity = Math.max(1, Math.min(10, Math.round((whoopData.strain_score / 21) * 10)));
    }

    return autoFill;
  };

  return {
    isConnected: connectionInfo?.isConnected ?? false,
    isLoading,
    hasData: !!whoopData,
    autoFillData: getAutoFillData(),
    lastSync: connectionInfo?.lastSync ?? null,
    whoopData,
  };
}

export default useWhoopAutoFill;
